import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LogOut, RefreshCcw, Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export const ADMIN_IDS = {
  root: "admin-dashboard",
  logout: "admin-logout",
  refresh: "admin-refresh",
  leadRow: (id) => `admin-lead-${id}`,
  empty: "admin-leads-empty",
};

const formatDate = (d) => {
  try {
    return new Date(d).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return d;
  }
};

const AdminDashboard = () => {
  const { user, http, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/leads");
      setLeads(data);
      if (data.length && !selectedId) setSelectedId(data[0].id);
    } catch {
      toast.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "TWGAS · Admin";
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doLogout = async () => {
    await logout();
    toast.success("Signed out.");
    navigate("/admin/login", { replace: true });
  };

  const selected = leads.find((l) => l.id === selectedId) || null;

  return (
    <main
      data-testid={ADMIN_IDS.root}
      className="min-h-screen bg-[#0A0A0A] pt-32 md:pt-40 pb-24"
    >
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="num-tag mb-3">
              / ADMIN · SIGNED IN AS {user?.email}
            </div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="font-display uppercase text-5xl md:text-7xl leading-[0.9] tracking-tight"
            >
              Lead
              <br />
              <span className="stroke-fire">command center.</span>
            </motion.h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid={ADMIN_IDS.refresh}
              className="btn-ghost"
              title="Refresh"
            >
              <RefreshCcw size={16} />
              Refresh
            </button>
            <button
              onClick={doLogout}
              data-testid={ADMIN_IDS.logout}
              className="btn-fire"
            >
              <LogOut size={16} />
              Sign Out
            </button>
          </div>
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { k: leads.length, v: "Total Leads" },
            {
              k: leads.filter(
                (l) =>
                  new Date(l.created_at).getTime() >
                  Date.now() - 7 * 24 * 60 * 60 * 1000
              ).length,
              v: "Last 7 Days",
            },
            {
              k: new Set(leads.map((l) => (l.sport || "").toLowerCase()).filter(Boolean))
                .size,
              v: "Sports",
            },
            {
              k: new Set(leads.map((l) => (l.level || "").toLowerCase()).filter(Boolean))
                .size,
              v: "Levels",
            },
          ].map((s) => (
            <div key={s.v} className="border border-white/10 p-5">
              <div className="font-display text-5xl leading-none text-[#FF4D00]">
                {s.k}
              </div>
              <div className="mt-2 font-mono-plex text-[10px] tracking-[0.28em] uppercase text-zinc-500">
                {s.v}
              </div>
            </div>
          ))}
        </div>

        {/* Split view */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {/* List */}
          <div className="md:col-span-5 border border-white/10">
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="font-mono-plex text-[10px] tracking-[0.28em] text-zinc-500 uppercase">
                / All Leads
              </div>
              {loading && (
                <div className="font-mono-plex text-[10px] tracking-[0.28em] text-[#FF4D00] uppercase">
                  Loading…
                </div>
              )}
            </div>
            <div className="max-h-[560px] overflow-auto divide-y divide-white/5">
              {!loading && leads.length === 0 && (
                <div
                  data-testid={ADMIN_IDS.empty}
                  className="p-8 text-center text-zinc-500 font-mono-plex text-xs tracking-[0.2em] uppercase"
                >
                  No leads yet.
                </div>
              )}
              {leads.map((l) => {
                const active = l.id === selectedId;
                return (
                  <button
                    key={l.id}
                    data-testid={ADMIN_IDS.leadRow(l.id)}
                    onClick={() => setSelectedId(l.id)}
                    className={`w-full text-left px-4 py-4 flex items-start justify-between gap-3 transition-colors ${
                      active
                        ? "bg-white/[0.04] border-l-2 border-[#FF4D00]"
                        : "hover:bg-white/[0.02] border-l-2 border-transparent"
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="font-display uppercase text-xl leading-none tracking-tight truncate">
                        {l.name}
                      </div>
                      <div className="mt-1 text-xs text-zinc-500 truncate">
                        {l.email}
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-[10px] font-mono-plex tracking-[0.2em] uppercase text-zinc-500">
                        {l.sport && <span>{l.sport}</span>}
                        {l.sport && l.level && <span>·</span>}
                        {l.level && <span>{l.level}</span>}
                      </div>
                    </div>
                    <div className="font-mono-plex text-[10px] tracking-[0.2em] text-zinc-500 uppercase shrink-0">
                      {formatDate(l.created_at)}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Detail */}
          <div className="md:col-span-7 border border-white/10 min-h-[400px]">
            {selected ? (
              <div className="p-6 md:p-8">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="num-tag mb-3">/ LEAD DETAIL</div>
                    <h2 className="font-display uppercase text-4xl md:text-5xl leading-none tracking-tight">
                      {selected.name}
                    </h2>
                    <div className="mt-3 text-zinc-400 text-sm">
                      {formatDate(selected.created_at)}
                    </div>
                  </div>
                  <a
                    href={`mailto:${selected.email}?subject=TWGAS%20Athletics%20-%20Follow%20up`}
                    className="btn-ghost"
                  >
                    <Mail size={14} />
                    Reply
                  </a>
                </div>

                <dl className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  {[
                    ["Email", selected.email],
                    ["Sport", selected.sport || "—"],
                    ["Level", selected.level || "—"],
                    ["Lead ID", selected.id],
                  ].map(([k, v]) => (
                    <div key={k} className="border-t border-white/10 pt-3">
                      <dt className="font-mono-plex text-[10px] tracking-[0.28em] uppercase text-zinc-500">
                        {k}
                      </dt>
                      <dd className="mt-1 text-white break-all">{v}</dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-10 border-t border-white/10 pt-6">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare size={14} className="text-[#FF4D00]" />
                    <span className="font-mono-plex text-[10px] tracking-[0.28em] uppercase text-zinc-500">
                      / Message
                    </span>
                  </div>
                  <p className="text-zinc-200 leading-relaxed whitespace-pre-wrap">
                    {selected.message}
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-8 text-zinc-500 font-mono-plex text-xs tracking-[0.2em] uppercase h-full flex items-center justify-center">
                Select a lead to view details.
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default AdminDashboard;
